import { Ref, ref } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import isQuoteCartEnabledGql from '~/modules/GraphQL/customQueries/requestQuote/isQuoteCartEnabled.gql';
import moveInCartGql from '~/modules/GraphQL/customQueries/requestQuote/moveInCart.gql';
import moveInQuoteGql from '~/modules/GraphQL/customQueries/requestQuote/moveInQuote.gql';
import createEmptyQuoteCartGql from '~/modules/GraphQL/customQueries/requestQuote/createEmptyQuoteCart.gql';
import addProductsToQuoteCartGql from '~/modules/GraphQL/customQueries/requestQuote/addProductsToQuoteCart.gql';
import updateQuoteRemarksGql from '~/modules/GraphQL/customQueries/requestQuote/updateQuoteRemarks.gql';
import removeItemFromQuoteCartGql from '~/modules/GraphQL/customQueries/requestQuote/removeItemFromQuoteCart.gql';
import updateQuoteItemsInCartGql from '~/modules/GraphQL/customQueries/requestQuote/updateQuoteItemsInCart.gql';
import cancelQuoteGql from '~/modules/GraphQL/customQueries/requestQuote/cancelQuote.gql';
import customerQuoteCartGql from '~/modules/GraphQL/customQueries/requestQuote/customerQuoteCart.gql';
import clearQuoteGql from '~/modules/GraphQL/customQueries/requestQuote/clearQuote.gql';
import submitQuoteGql from '~/modules/GraphQL/customQueries/requestQuote/submitQuote.gql';
import customerRequestQuotesGql from '~/modules/GraphQL/customQueries/requestQuote/customerRequestQuotes.gql';
import {
  AddProductsToQuoteCartInput,
  CancelQuoteInput,
  CreateEmptyQuoteCartInput,
  MoveInCartInput,
  MoveInQuoteInput,
  RemoveItemFromCartInput,
  SubmitQuoteInput,
  UpdateQuoteItemsInput,
  UpdateQuoteRemarksInput,
} from '~/modules/GraphQL/types';
import { useApi } from '~/composables';
import {
  SearchCustomerRequestQuotesParams,
  UseRequestQuoteAPIErrors,
  UseRequestQuoteAPIInterface,
} from './useRequestQuoteAPI';

const isQuoteCartEnabled = ref(false);

export function useRequestQuoteAPI(): UseRequestQuoteAPIInterface {
  const { query, mutate } = useApi();
  const loading = ref(false);
  const error: Ref<UseRequestQuoteAPIErrors> = ref({
    isQuoteCartEnabled: null,
    moveInCart: null,
    moveInQuote: null,
    createEmptyQuoteCart: null,
    addProductsToQuoteCart: null,
    updateQuoteRemarks: null,
    removeItemFromQuoteCart: null,
    updateQuoteItemsInCart: null,
    cancelQuote: null,
    customerQuoteCart: null,
    clearQuote: null,
    submitQuote: null,
    searchCustomerRequestQuotes: null,
  });

  const checkIsQuoteCartEnabled = async () => {
    Logger.debug('useRequestQuote/checkIsQuoteCartEnabled');

    try {
      loading.value = true;

      const { data }: any = await query(isQuoteCartEnabledGql);

      Logger.debug('[Result]:', { data });

      isQuoteCartEnabled.value = data?.isQuoteCartEnabled ?? false;
      error.value.isQuoteCartEnabled = null;
    } catch (err) {
      error.value.isQuoteCartEnabled = err;
      Logger.error('useRequestQuote/checkIsQuoteCartEnabled', err);
    } finally {
      loading.value = false;
    }
  };

  const moveInCart = async (input: MoveInCartInput) => {
    Logger.debug('useRequestQuote/moveInCart');
    let moveInCartOutput = false;

    try {
      loading.value = true;

      const { data }: any = await mutate(moveInCartGql, input);
      Logger.debug('[Result]:', { data });

      error.value.moveInCart = null;
      moveInCartOutput = await data?.moveInCart;
    } catch (err) {
      error.value.moveInCart = err;
      Logger.error('useRequestQuote/moveInCart', err);
    } finally {
      loading.value = false;
    }
    return moveInCartOutput;
  };

  const moveInQuote = async (input: MoveInQuoteInput) => {
    Logger.debug('useRequestQuote/moveInQuote');
    let moveInQuoteOutput = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(moveInQuoteGql, input);
      Logger.debug('[Result]:', { data });
      moveInQuoteOutput = await data.moveInQuote;

      error.value.moveInQuote = null;
    } catch (err) {
      error.value.moveInQuote = err;
      Logger.error('useRequestQuote/moveInQuote', err);
    } finally {
      loading.value = false;
    }

    return moveInQuoteOutput;
  };

  const createEmptyQuoteCart = async (input: CreateEmptyQuoteCartInput) => {
    Logger.debug('useRequestQuote/createEmptyQuoteCart');
    let emptyQuoteCart = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(createEmptyQuoteCartGql, input);
      Logger.debug('[Result]:', { data });
      emptyQuoteCart = await data.createEmptyQuoteCart;

      error.value.createEmptyQuoteCart = null;
    } catch (err) {
      error.value.createEmptyQuoteCart = err;
      Logger.error('useRequestQuote/createEmptyQuoteCart', err);
    } finally {
      loading.value = false;
    }
    return emptyQuoteCart;
  };

  const addProductsToQuoteCart = async (input: AddProductsToQuoteCartInput) => {
    Logger.debug('useRequestQuote/addProductsToQuoteCart');
    let addProductsToQuoteCartOutput = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(addProductsToQuoteCartGql, input);
      Logger.debug('[Result]:', { data });

      error.value.addProductsToQuoteCart = null;
      addProductsToQuoteCartOutput = await data?.addProductsToQuoteCart;
    } catch (err) {
      error.value.addProductsToQuoteCart = err;
      Logger.error('useRequestQuote/addProductsToQuoteCart', err);
    } finally {
      loading.value = false;
    }
    return addProductsToQuoteCartOutput;
  };

  const updateQuoteRemarks = async (input: UpdateQuoteRemarksInput) => {
    Logger.debug('useRequestQuote/updateQuoteRemarks');

    try {
      loading.value = true;

      const { data } = await mutate(updateQuoteRemarksGql, input);
      Logger.debug('[Result]:', { data });

      error.value.updateQuoteRemarks = null;
    } catch (err) {
      error.value.updateQuoteRemarks = err;
      Logger.error('useRequestQuote/updateQuoteRemarks', err);
    } finally {
      loading.value = false;
    }
  };

  const removeItemFromQuoteCart = async (input: RemoveItemFromCartInput) => {
    Logger.debug('useRequestQuote/removeItemFromQuoteCart');
    let removeItemFromQuoteCartOutput = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(removeItemFromQuoteCartGql, { input });
      Logger.debug('[Result]:', { data });

      error.value.removeItemFromQuoteCart = null;
      removeItemFromQuoteCartOutput = await data?.removeItemFromQuoteCart;
    } catch (err) {
      error.value.removeItemFromQuoteCart = err;
      Logger.error('useRequestQuote/removeItemFromQuoteCart', err);
    } finally {
      loading.value = false;
    }

    return removeItemFromQuoteCartOutput;
  };

  const updateQuoteItemsInCart = async (input: UpdateQuoteItemsInput) => {
    Logger.debug('useRequestQuote/updateQuoteItemsInCart');
    let updateQuoteItemsInCartOutput = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(updateQuoteItemsInCartGql, { input });
      Logger.debug('[Result]:', { data });

      error.value.updateQuoteItemsInCart = null;
      updateQuoteItemsInCartOutput = await data?.updateQuoteItemsInCart;
    } catch (err) {
      error.value.updateQuoteItemsInCart = err;
      Logger.error('useRequestQuote/updateQuoteItemsInCart', err);
    } finally {
      loading.value = false;
    }

    return updateQuoteItemsInCartOutput;
  };

  const cancelQuote = async (input: CancelQuoteInput) => {
    Logger.debug('useRequestQuote/cancelQuote');

    try {
      loading.value = true;

      const { data } = await mutate(cancelQuoteGql, input);
      Logger.debug('[Result]:', { data });

      error.value.cancelQuote = null;
    } catch (err) {
      error.value.cancelQuote = err;
      Logger.error('useRequestQuote/cancelQuote', err);
    } finally {
      loading.value = false;
    }
  };

  const customerQuoteCart = async () => {
    Logger.debug('useRequestQuote/customerQuoteCart');
    let customerQuoteCartOutput = null;

    try {
      loading.value = true;

      const { data }: any = await query(customerQuoteCartGql);

      Logger.debug('[Result]:', { data });

      customerQuoteCartOutput = await data?.customerQuoteCart;
      error.value.customerQuoteCart = null;
    } catch (err) {
      error.value.customerQuoteCart = err;
      Logger.error('useRequestQuote/customerQuoteCart', err);
    } finally {
      loading.value = false;
    }

    return customerQuoteCartOutput;
  };

  const clearQuote = async (quoteId: string) => {
    Logger.debug('useRequestQuote/clearQuote');
    let clearQuoteOutput = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(clearQuoteGql, { quoteId });

      Logger.debug('[Result]:', { data });

      clearQuoteOutput = await data?.clearQuote;
      error.value.clearQuote = null;
    } catch (err) {
      error.value.clearQuote = err;
      Logger.error('useRequestQuote/clearQuote', err);
    } finally {
      loading.value = false;
    }

    return clearQuoteOutput;
  };

  const submitQuote = async (input: SubmitQuoteInput) => {
    Logger.debug('useRequestQuote/submitQuote');
    let submitQuoteOutput = null;

    try {
      loading.value = true;

      const { data }: any = await mutate(submitQuoteGql, { input });
      Logger.debug('[Result]:', { data });

      error.value.submitQuote = null;
      submitQuoteOutput = await data?.submitQuote;
    } catch (err) {
      error.value.submitQuote = err;
      Logger.error('useRequestQuote/submitQuote', err);
    } finally {
      loading.value = false;
    }
    return submitQuoteOutput;
  };

  const searchCustomerRequestQuotes = async (params: SearchCustomerRequestQuotesParams) => {
    let results = null;

    try {
      loading.value = true;

      Logger.debug('[Magento] search user request quotes', { params });

      const { data }: any = await query(customerRequestQuotesGql, params);

      Logger.debug('[Result]:', { data });

      results = data?.customer?.request_quotes ?? null;
      error.value.searchCustomerRequestQuotes = null;
    } catch (err) {
      error.value.searchCustomerRequestQuotes = err;
      Logger.error('useRelatedProducts/search', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  return {
    loading,
    error,
    isQuoteCartEnabled,
    checkIsQuoteCartEnabled,
    moveInCart,
    moveInQuote,
    createEmptyQuoteCart,
    addProductsToQuoteCart,
    updateQuoteRemarks,
    removeItemFromQuoteCart,
    updateQuoteItemsInCart,
    cancelQuote,
    customerQuoteCart,
    clearQuote,
    submitQuote,
    searchCustomerRequestQuotes,
  };
}
